/**
 * Doctor Patient Queue — manage waiting patients.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appointmentAPI, consultationAPI, doctorAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const DoctorQueue = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState('');

  const loadQueue = useCallback(async () => {
    try {
      const res = await doctorAPI.getQueue();
      setQueue(Array.isArray(res.data) ? res.data : (res.data?.queue || []));
    } catch {
      toast.error('Failed to load patient queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Live updates: new patient added to queue
  const { on } = useNotifications();
  useEffect(() => {
    return on('queue_update', loadQueue);
  }, [loadQueue, on]);

  useEffect(() => {
    return on('appointment_update', loadQueue);
  }, [loadQueue, on]);

  const openConsultation = (patient) => {
    if (patient.room_id) {
      navigate(`/consultation?room=${patient.room_id}&appointment=${patient.id}`);
      return;
    }

    navigate(`/doctor/consultation/start?appointment=${patient.id}`);
  };

  const markDone = async (patient) => {
    setProcessingId(patient.id);
    try {
      if (patient.room_id) {
        await consultationAPI.end({ room_id: patient.room_id, notes: '' });
      } else {
        await appointmentAPI.updateStatus(patient.id, { status: 'completed' });
      }
      toast.success(`${patient.patient_name || 'Patient'} removed from queue.`);
      await loadQueue();
    } catch (err) {
      toast.error(err?.message || 'Unable to mark consultation as done.');
    } finally {
      setProcessingId('');
    }
  };

  return (
    <Layout title="Patient Queue" role="doctor" showBack>
      <div className="p-4 pb-24">
        {/* Stats bar */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2 text-sm bg-accent-50 text-accent-700 px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-accent-500 rounded-full"></span>
            {queue.length} waiting
          </div>
          <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            {queue.filter(q => (q.appointment_type || q.type) === 'emergency').length} emergency
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}
          </div>
        ) : queue.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="font-medium">Queue is empty</p>
            <p className="text-sm mt-1">No patients are currently waiting.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {queue.map((patient, idx) => (
                <div key={patient.id} className={`card flex items-center gap-3 ${(patient.appointment_type || patient.type) === 'emergency' ? 'border-red-200 bg-red-50' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                    (patient.appointment_type || patient.type) === 'emergency' ? 'bg-red-500' : idx === 0 ? 'bg-accent-500' : 'bg-gray-400'
                }`}>
                  #{idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{patient.patient_name || 'Patient'}</p>
                  <p className="text-xs text-gray-500">
                    {patient.appointment_type || patient.type || 'Regular'} • {patient.appointment_date ? new Date(patient.appointment_date).toLocaleDateString() : 'Today'}
                  </p>
                  {patient.symptoms && (
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{patient.symptoms}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  {(patient.appointment_type || patient.type) === 'emergency' && (
                    <span className="badge-danger text-xs">URGENT</span>
                  )}
                  <button
                    onClick={() => navigate(`/doctor/patients/${patient.patient_uid}`)}
                    className="px-3 py-1.5 bg-white text-gray-700 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    View Profile
                  </button>
                  <button
                    onClick={() => openConsultation(patient)}
                    className="px-3 py-1.5 bg-accent-500 text-white text-xs font-medium rounded-lg hover:bg-accent-600 transition-colors"
                  >
                    {patient.room_id || patient.status === 'in_progress' ? 'Resume' : 'Start Consult'}
                  </button>
                  <button
                    onClick={() => markDone(patient)}
                    disabled={processingId === patient.id}
                    className="px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 disabled:opacity-60 transition-colors"
                  >
                    {processingId === patient.id ? 'Saving...' : 'Mark Done'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DoctorQueue;
